import { useCallback, useEffect, useState } from 'react';
import { useDonationBlockProps } from '../DonationBlockProvider';

interface AuthorizenetVerifyResponse {
    success: true;
    message: string;
}
interface IUseAuthorizeUpdateResult {
    isSuccess: boolean;
    isError: boolean;
    message: string;
    isLoading: boolean;
    finalizePayment: (props: {
        submissionId: string;
        transactionId: string;
    }) => Promise<void>;
}

const useAuthorizeUpdate = (): IUseAuthorizeUpdateResult => {
    const [response, setResponse] = useState({
        isSuccess: false,
        isError: false,
        message: '',
    });

    const { baseURL } = useDonationBlockProps();

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const finalizePayment: IUseAuthorizeUpdateResult['finalizePayment'] = useCallback(async({ submissionId, transactionId }) => {
        setIsLoading(true);

        try {
            const finalizeAPI = `${baseURL}/api/authorizenet/authorize`;

            const requestBody = new FormData();
            requestBody.append('Id', submissionId);
            requestBody.append('TransId', transactionId);

            const getCRMStatus = await fetch(finalizeAPI, {
                method: 'POST',
                body: requestBody,
            });

            if (!getCRMStatus.ok) {
                throw new Error('Failed to fetch Authorize.net token');
            }

            const data = await getCRMStatus.json() as AuthorizenetVerifyResponse;

            setResponse({
                isSuccess: data.success,
                isError: !data.success,
                message: data.message,
            });

            setIsLoading(false);
        } catch (error) {
            console.error('[Authorize.Net] ', error);
            throw error;
        }
    }, [baseURL]);

    useEffect(() => {
        return () => {
            setIsLoading(false);
            setResponse({
                isSuccess: false,
                isError: false,
                message: '',
            });
        };
    }, [finalizePayment]);

    return {
        ...response,
        isLoading,
        finalizePayment,
    };
};

export default useAuthorizeUpdate;
